import { Helmet } from 'react-helmet-async';

const SEO = ({
  title,
  description,
  keywords,
  image,
  url,
  type = 'website',
  author = 'Aquargin',
  publishedTime,
  modifiedTime,
  article = false,
  noindex = false
}) => {
  const siteUrl = 'https://aquargin.com';
  const defaultImage = `${siteUrl}/images/og-image.png`;
  const defaultDescription = 'Transform Your Ideas into Reality with Aquargin\'s composable enterprise automation platform. Build modular, API-first business capabilities with no-code tools.';
  
  const pageTitle = title ? `${title} | Aquargin` : 'Aquargin - Transform Your Ideas into Reality | No-Code Platform';
  const pageDescription = description || defaultDescription;
  const pageImage = image ? `${siteUrl}${image}` : defaultImage;
  const pageUrl = url ? `${siteUrl}${url}` : siteUrl;

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${siteUrl}/#organization`,
        "name": "Aquargin",
        "url": siteUrl,
        "sameAs": [
          "https://www.youtube.com/@nocodeAquargin",
          "https://www.linkedin.com/company/aquargin",
          "https://twitter.com/aquargin"
        ],
        "logo": {
          "@type": "ImageObject",
          "url": `${siteUrl}/images/aquargin-logo.png`
        },
        "description": "Composable enterprise automation platform enabling no-code business solutions",
        "foundingDate": "2020",
        "contactPoint": {
          "@type": "ContactPoint",
          "telephone": "+91 70175 92307",
          "contactType": "customer service",
          "email": "support@aquargin.com",
          "availableLanguage": ["English"],
          "areaServed": "Worldwide"
        },
        "address": {
          "@type": "PostalAddress",
          "addressCountry": "IN"
        }
      },
      {
        "@type": "WebSite",
        "@id": `${siteUrl}/#website`,
        "url": siteUrl,
        "name": "Aquargin",
        "description": pageDescription,
        "publisher": {
          "@id": `${siteUrl}/#organization`
        },
        "potentialAction": {
          "@type": "SearchAction",
          "target": {
            "@type": "EntryPoint",
            "urlTemplate": `${siteUrl}/search?q={search_term_string}`
          },
          "query-input": "required name=search_term_string"
        },
        "inLanguage": "en-US"
      },
      {
        "@type": "WebPage",
        "@id": `${pageUrl}/#webpage`,
        "url": pageUrl,
        "name": pageTitle,
        "isPartOf": {
          "@id": `${siteUrl}/#website`
        },
        "about": {
          "@id": `${siteUrl}/#organization`
        },
        "description": pageDescription,
        "inLanguage": "en-US",
        "potentialAction": [
          {
            "@type": "ReadAction",
            "target": [pageUrl]
          }
        ]
      }
    ]
  };

  // Add article structured data if it's an article/blog post
  if (article) {
    structuredData["@graph"].push({
      "@type": "Article",
      "@id": `${pageUrl}/#article`,
      "isPartOf": {
        "@id": `${pageUrl}/#webpage`
      },
      "author": {
        "@type": "Organization",
        "@id": `${siteUrl}/#organization`
      },
      "headline": title,
      "description": pageDescription,
      "image": pageImage,
      "datePublished": publishedTime,
      "dateModified": modifiedTime || publishedTime,
      "mainEntityOfPage": {
        "@id": `${pageUrl}/#webpage`
      },
      "publisher": {
        "@id": `${siteUrl}/#organization`
      }
    });
  }

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="author" content={author} />
      <link rel="canonical" href={pageUrl} />
      
      {/* Robots */}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      )}
      
      {/* Open Graph Tags */}
      <meta property="og:locale" content="en_US" />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={pageImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={title || 'Aquargin Platform'} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:site_name" content="Aquargin" />
      
      {/* Article specific Open Graph */}
      {article && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {article && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      {article && (
        <meta property="article:author" content="Aquargin" />
      )}
      
      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@aquargin" />
      <meta name="twitter:creator" content="@aquargin" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={pageImage} />
      <meta name="twitter:image:alt" content={title || 'Aquargin Platform'} />
      
      {/* Additional SEO Meta Tags */}
      <meta name="theme-color" content="#00b8e6" />
      <meta name="msapplication-TileColor" content="#00b8e6" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content="Aquargin" />
      <meta name="application-name" content="Aquargin" />
      
      {/* Geo Meta Tags */}
      <meta name="geo.region" content="IN" />
      <meta name="geo.placename" content="India" />
      
      {/* Language and Locale */}
      <meta httpEquiv="content-language" content="en-us" />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
      
      {/* Preconnect for Performance */}
      <link rel="preconnect" href="https://www.google-analytics.com" />
      <link rel="preconnect" href="https://www.googletagmanager.com" />
      <link rel="dns-prefetch" href="https://fonts.googleapis.com" />
      <link rel="dns-prefetch" href="https://fonts.gstatic.com" />
    </Helmet>
  );
};

export default SEO;
